import { useState, useRef, useEffect, useCallback } from "react";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { toast } from "sonner";
import axios from "axios";
import { 
  Shield, Translate, 
  CloudArrowDown, FilePdf, Trash, Check, Eye, EyeSlash,
  Gear, X, CaretRight, Percent, PencilSimple, Plus, Bell,
  ChartLineUp, Upload, FileText, FileXls, Tag, Warning
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { getApiErrorMessage } from "@/lib/apiError";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/PageHeader";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSettings } from "@/contexts/SettingsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Biometric Icon component
function BiometricIcon({ size }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a5 5 0 0 0-5 5v3a5 5 0 0 0 10 0V7a5 5 0 0 0-5-5z"/>
      <path d="M12 14v4"/>
      <path d="M8 18h8"/>
      <circle cx="12" cy="8" r="1"/>
    </svg>
  );
}

// Numeric keypad component
function NumericKeypad({ onInput, onBackspace }) {
  return (
    <div className="grid grid-cols-3 gap-3 max-w-[240px] mx-auto">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, 'back'].map((key, idx) => (
        <button
          key={idx}
          onClick={() => {
            if (key === 'back') onBackspace();
            else if (key !== null) onInput(String(key));
          }}
          disabled={key === null}
          className={`
            h-14 rounded-xl text-xl font-semibold transition-all
            ${key === null ? 'invisible' : ''}
            ${key === 'back' 
              ? 'bg-stone-200 dark:bg-stone-700 text-stone-600 dark:text-stone-300 hover:bg-stone-300' 
              : 'bg-stone-100 dark:bg-stone-800 text-stone-900 dark:text-white hover:bg-[#00A19A]/20 active:bg-[#00A19A]/30'
            }
          `}
          data-testid={`keypad-${key}`}
        >
          {key === 'back' ? '⌫' : key}
        </button>
      ))}
    </div>
  );
}

export default function Settings() {
  const { 
    settings, updateSetting, setPin, removePin, t, languages, clearCache,
    biometricAvailable, enableBiometric, disableBiometric, isReadOnly
  } = useSettings();
  
  // Active section state
  const [activeSection, setActiveSection] = useState(null);
  
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pinAction, setPinAction] = useState('set');
  const [pinStep, setPinStep] = useState(1);
  const [pinValue, setPinValue] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [currentPin, setCurrentPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [enablingBiometric, setEnablingBiometric] = useState(false);
  
  // PDF Management for Chatbot
  const [pdfTexts, setPdfTexts] = useState([]);
  const [loadingPdfs, setLoadingPdfs] = useState(false);
  const [loadingPdfExtraction, setLoadingPdfExtraction] = useState(false);
  const [customPdfUrl, setCustomPdfUrl] = useState('');
  const [customPdfName, setCustomPdfName] = useState('');
  const [loadingCustomPdf, setLoadingCustomPdf] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const pdfFileInputRef = useRef(null);

  // Rappel Management
  const [rappelTargets, setRappelTargets] = useState([]);
  const [loadingRappelTargets, setLoadingRappelTargets] = useState(false);
  const [uploadingRappelFile, setUploadingRappelFile] = useState(false);
  const rappelFileInputRef = useRef(null);
  const [editingRappelNumber, setEditingRappelNumber] = useState(null);
  const [rappelEditForm, setRappelEditForm] = useState(null);
  const [showRappelAddForm, setShowRappelAddForm] = useState(false);
  const emptyRappelTiers = () => ([{ objetivo: '', percentual: '' }, { objetivo: '', percentual: '' }, { objetivo: '', percentual: '' }]);
  const [rappelNewForm, setRappelNewForm] = useState({ client_number: '', client_name: '', tiers: emptyRappelTiers() });

  // Alert Settings
  const [alertSettings, setAlertSettings] = useState({ visit_frequency_days: 90 });
  const [savingAlertSettings, setSavingAlertSettings] = useState(false);

  // Vendas (GPP Sales) Data Upload
  const [vendasFiles, setVendasFiles] = useState([]);
  const [parsingVendas, setParsingVendas] = useState(false);
  const [vendasLastUpdated, setVendasLastUpdated] = useState(null);
  const vendasFileInputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/sales/vendas`).then(res => {
      setVendasLastUpdated(res.data?.last_updated || null);
    }).catch(() => {});
  }, []);

  // Lista de Preços - Actualização via Excel
  const [priceListMeta, setPriceListMeta] = useState(null);
  const [uploadingPriceList, setUploadingPriceList] = useState(false);
  const [priceListPendingFile, setPriceListPendingFile] = useState(null);
  const [priceListRemovalPreview, setPriceListRemovalPreview] = useState(null); // { to_remove, to_add_count, to_update_count }
  const [showPriceListRemovalDialog, setShowPriceListRemovalDialog] = useState(false);
  const priceListFileInputRef = useRef(null);

  const fetchPriceListMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/price-products-meta/last-update`);
      setPriceListMeta(res.data?.last_updated ? res.data : null);
    } catch (error) {
      console.error("Error fetching price list metadata:", error);
    }
  }, []);

  useEffect(() => {
    fetchPriceListMeta();
  }, [fetchPriceListMeta]);

  const submitPriceListImport = async (file, confirmRemoval) => {
    setUploadingPriceList(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(
        `${API}/price-products/import${confirmRemoval ? '?confirm_removal=true' : ''}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (res.data.requires_confirmation) {
        setPriceListPendingFile(file);
        setPriceListRemovalPreview(res.data);
        setShowPriceListRemovalDialog(true);
      } else {
        toast.success(`Lista de preços atualizada: ${res.data.added} adicionado(s), ${res.data.updated} atualizado(s)${res.data.removed ? `, ${res.data.removed} removido(s)` : ''}`);
        setShowPriceListRemovalDialog(false);
        setPriceListPendingFile(null);
        setPriceListRemovalPreview(null);
        await fetchPriceListMeta();
      }
    } catch (error) {
      toast.error("Erro ao importar lista de preços: " + getApiErrorMessage(error));
    } finally {
      setUploadingPriceList(false);
      if (priceListFileInputRef.current) priceListFileInputRef.current.value = '';
    }
  };

  const handlePriceListFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
      toast.error("Por favor selecione um ficheiro Excel (.xlsx)");
      return;
    }
    submitPriceListImport(file, false);
  };

  const confirmPriceListRemoval = () => {
    if (priceListPendingFile) {
      submitPriceListImport(priceListPendingFile, true);
    }
  };

  const cancelPriceListRemoval = () => {
    setShowPriceListRemovalDialog(false);
    setPriceListPendingFile(null);
    setPriceListRemovalPreview(null);
  };

  const handleVendasFileUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newFiles = [];
    for (const file of files) {
      const reader = new FileReader();
      reader.onload = (event) => {
        newFiles.push({
          name: file.name,
          type: file.type,
          size: file.size,
          content: event.target.result
        });
        if (newFiles.length === files.length) {
          setVendasFiles(prev => [...prev, ...newFiles]);
          toast.success(`${files.length} ficheiro(s) carregado(s)`);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const removeVendasFile = (index) => {
    setVendasFiles(prev => prev.filter((_, i) => i !== index));
  };

  const parseVendasData = async () => {
    if (vendasFiles.length === 0) {
      toast.error("Por favor, carregue ficheiros GPP para análise");
      return;
    }
    setParsingVendas(true);
    try {
      const response = await axios.post(`${API}/sales/vendas/parse`, {
        files: vendasFiles.map(f => ({ name: f.name, content: f.content }))
      });
      setVendasLastUpdated(response.data?.last_updated || null);
      setVendasFiles([]);
      toast.success("Dados de vendas atualizados com sucesso! Consulte a Análise de Vendas.");
    } catch (error) {
      console.error("Parse error:", error);
      toast.error("Erro ao processar: " + getApiErrorMessage(error));
    } finally {
      setParsingVendas(false);
    }
  };

  const getVendasFileIcon = (filename) => {
    if (filename.endsWith('.pdf')) return <FilePdf size={24} className="text-red-500" />;
    if (filename.endsWith('.xlsx') || filename.endsWith('.xls')) return <FileXls size={24} className="text-green-600" />;
    return <FileText size={24} className="text-stone-500" />;
  };
  
  // Default PDF URLs for Gresart catalogs
  const defaultPdfCatalogs = [
    {
      name: "Catálogo Geral Gresart 2026",
      url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_client-products/artifacts/n1uw1rnz_Catalogo-Geral--2026%20%281%29.pdf",
      type: "gresart_catalog"
    },
    {
      name: "GR Merchandising Geral 2025",
      url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_client-products/artifacts/zlrg6lq3_GR%20Merchandising%20Geral%202025.11_compressed%20%281%29.pdf",
      type: "merchandising_catalog"
    },
    {
      name: "GR Stardust 2026",
      url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_client-products/artifacts/gusmiesr_GR%20Stardust%202026.pdf",
      type: "merchandising_catalog"
    },
    {
      name: "GR Stardust 2026 - DECOR NOVA",
      url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_client-products/artifacts/3zw58pi1_GR%20Stardust%202026%20-%20DECOR%20NOVA.pdf",
      type: "merchandising_catalog"
    },
    {
      name: "GR XL Stardust 2026",
      url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_client-products/artifacts/chlnqcgy_GR%20XL%20Stardust%202026.pdf",
      type: "merchandising_catalog"
    },
    {
      name: "GR XL Stardust 2026 - DECOR NOVA",
      url: "https://customer-assets-lxgj4vgw.emergentagent.net/job_client-products/artifacts/e6ejscz7_GR%20XL%20Stardust%202026%20-%20DECOR%20NOVA.pdf",
      type: "merchandising_catalog"
    }
  ];
  
  // Fetch indexed PDFs
  const fetchPdfTexts = async () => {
    setLoadingPdfs(true);
    try {
      const response = await axios.get(`${API}/pdf/extracted-texts`);
      setPdfTexts(response.data.pdfs || []);
    } catch (error) {
      console.error("Error fetching PDF texts:", error);
    } finally {
      setLoadingPdfs(false);
    }
  };
  
  // Load all default PDFs
  const loadAllDefaultPdfs = async () => {
    setLoadingPdfExtraction(true);
    
    let loaded = 0;
    let failed = 0;
    const failedNames = [];
    
    for (const catalog of defaultPdfCatalogs) {
      toast.loading(`A carregar: ${catalog.name}...`, { id: "pdf-loading" });
      
      try {
        const response = await axios.post(`${API}/pdf/extract-from-url`, {
          url: catalog.url,
          pdf_name: catalog.name,
          pdf_type: catalog.type
        }, { timeout: 300000 }); // 5 minutes timeout
        
        if (response.data.chars_extracted > 0) {
          loaded++;
          toast.success(`✓ ${catalog.name}`, { id: "pdf-loading", duration: 1500 });
        } else {
          // PDF was image-based, still count as processed
          loaded++;
          toast.success(`✓ ${catalog.name} (sem texto)`, { id: "pdf-loading", duration: 1500 });
        }
      } catch (error) {
        console.error(`Error loading ${catalog.name}:`, error);
        failed++;
        failedNames.push(catalog.name);
        toast.error(`✗ ${catalog.name}`, { id: "pdf-loading", duration: 1500 });
      }
      
      // Small delay between requests to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    toast.dismiss("pdf-loading");
    
    if (loaded > 0 && failed === 0) {
      toast.success(`Todos os ${loaded} catálogos carregados!`);
    } else if (loaded > 0) {
      toast.success(`${loaded} catálogos carregados`);
    }
    
    if (failed > 0) {
      toast.error(`${failed} falharam: ${failedNames.join(', ')}`, { duration: 5000 });
    }
    
    await fetchPdfTexts();
    setLoadingPdfExtraction(false);
  };
  
  // Clear all PDF texts
  const clearPdfTexts = async () => {
    try {
      await axios.delete(`${API}/pdf/extracted-texts`);
      setPdfTexts([]);
      toast.success("Textos de PDFs eliminados");
    } catch (error) {
      toast.error("Erro ao eliminar textos");
    }
  };
  
  // Delete a single PDF/Excel document
  const deletePdfText = async (pdfName) => {
    if (!window.confirm(`Eliminar o documento "${pdfName}"?`)) return;
    try {
      await axios.delete(`${API}/pdf/extracted-texts/${encodeURIComponent(pdfName)}`);
      setPdfTexts((prev) => prev.filter((p) => p.pdf_name !== pdfName));
      toast.success(`"${pdfName}" eliminado`);
    } catch (error) {
      toast.error("Erro ao eliminar documento");
    }
  };
  
  // Load custom PDF from URL
  const loadCustomPdf = async () => {
    if (!customPdfUrl || !customPdfName) {
      toast.error("Introduza o nome e URL do PDF");
      return;
    }
    
    setLoadingCustomPdf(true);
    toast.loading("A extrair texto do PDF...", { id: "custom-pdf" });
    
    try {
      const response = await axios.post(`${API}/pdf/extract-from-url`, {
        url: customPdfUrl,
        pdf_name: customPdfName,
        pdf_type: "price_list"
      }, { timeout: 180000 });
      
      toast.dismiss("custom-pdf");
      
      if (response.data.chars_extracted > 0) {
        toast.success(`PDF carregado: ${response.data.chars_extracted} caracteres extraídos`);
        setCustomPdfUrl('');
        setCustomPdfName('');
        await fetchPdfTexts();
      } else {
        toast.error("PDF não tem texto extraível (pode ser baseado em imagens)");
      }
    } catch (error) {
      toast.dismiss("custom-pdf");
      toast.error("Erro ao carregar PDF: " + getApiErrorMessage(error));
    } finally {
      setLoadingCustomPdf(false);
    }
  };
  
  // Upload one PDF/Excel file for chatbot, returns response data
  const uploadOneChatbotFile = async (file, docName) => {
    const fileName = file.name.toLowerCase();
    const isPdf = fileName.endsWith('.pdf');
    const formData = new FormData();
    formData.append('file', file);
    
    let response;
    if (isPdf) {
      formData.append('pdf_name', docName);
      formData.append('pdf_type', 'custom');
      response = await axios.post(`${API}/pdf/upload-and-extract`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000
      });
    } else {
      formData.append('doc_name', docName);
      formData.append('doc_type', 'price_list');
      response = await axios.post(`${API}/pdf/upload-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000
      });
    }
    return response.data;
  };
  
  // Upload PDF file(s) directly - supports selecting multiple files at once
  const handlePdfFileUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    
    const validFiles = files.filter(f => {
      const n = f.name.toLowerCase();
      return n.endsWith('.pdf') || n.endsWith('.xlsx') || n.endsWith('.xls');
    });
    
    if (validFiles.length === 0) {
      toast.error("Por favor selecione ficheiros PDF ou Excel (.xlsx)");
      return;
    }
    
    setUploadingPdf(true);
    let successCount = 0;
    let failCount = 0;
    
    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      const docName = file.name.replace(/\.(pdf|xlsx|xls)$/i, '');
      toast.loading(`A processar ${i + 1}/${validFiles.length}: ${docName}...`, { id: "file-upload" });
      try {
        const data = await uploadOneChatbotFile(file, docName);
        if (data.chars_extracted > 0) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        failCount++;
      }
    }
    
    toast.dismiss("file-upload");
    if (successCount > 0) {
      toast.success(`${successCount} documento(s) carregado(s) com sucesso${failCount > 0 ? `, ${failCount} falhou/falharam` : ''}`);
      await fetchPdfTexts();
    } else {
      toast.error("Nenhum ficheiro tinha texto extraível");
    }
    
    setUploadingPdf(false);
    if (pdfFileInputRef.current) {
      pdfFileInputRef.current.value = '';
    }
  };
  
  // Load PDF texts right away (fixes the "0 catálogos indexados" flash on the settings
  // grid) and again whenever the chatbot section itself is opened (keeps list fresh).
  useEffect(() => {
    fetchPdfTexts();
  }, []);

  useEffect(() => {
    if (activeSection === 'chatbot') {
      fetchPdfTexts();
    }
  }, [activeSection]);

  // Rappel Management handlers
  const fetchRappelTargets = async () => {
    setLoadingRappelTargets(true);
    try {
      const res = await axios.get(`${API}/rappel/targets`);
      setRappelTargets(res.data || []);
    } catch (error) {
      console.error("Error fetching rappel targets:", error);
    } finally {
      setLoadingRappelTargets(false);
    }
  };

  useEffect(() => {
    fetchRappelTargets();
  }, []);

  useEffect(() => {
    const fetchAlertSettings = async () => {
      try {
        const res = await axios.get(`${API}/alert-settings`);
        setAlertSettings(res.data);
      } catch (error) {
        console.error("Error fetching alert settings:", error);
      }
    };
    fetchAlertSettings();
  }, []);

  const saveAlertSettings = async (newSettings) => {
    setSavingAlertSettings(true);
    try {
      const res = await axios.put(`${API}/alert-settings`, newSettings);
      setAlertSettings(res.data);
      toast.success("Configurações de alertas guardadas");
    } catch (error) {
      toast.error("Erro ao guardar: " + getApiErrorMessage(error));
    } finally {
      setSavingAlertSettings(false);
    }
  };

  const handleRappelFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadingRappelFile(true);
    try {
      const res = await axios.post(`${API}/rappel/targets/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data.message);
      await fetchRappelTargets();
    } catch (error) {
      toast.error("Erro ao importar: " + getApiErrorMessage(error));
    } finally {
      setUploadingRappelFile(false);
      if (rappelFileInputRef.current) rappelFileInputRef.current.value = '';
    }
  };

  const startEditRappel = (target) => {
    setEditingRappelNumber(target.client_number);
    setRappelEditForm({
      client_name: target.client_name,
      tiers: target.tiers.map(t => ({ objetivo: String(t.objetivo), percentual: String((t.percentual * 100).toFixed(2)) }))
    });
  };

  const saveEditRappel = async () => {
    try {
      const payload = {
        client_number: editingRappelNumber,
        client_name: rappelEditForm.client_name,
        tiers: rappelEditForm.tiers.map(t => ({ objetivo: parseFloat(t.objetivo) || 0, percentual: (parseFloat(t.percentual) || 0) / 100 }))
      };
      await axios.put(`${API}/rappel/targets/${editingRappelNumber}`, payload);
      toast.success("Distribuidor atualizado");
      setEditingRappelNumber(null);
      await fetchRappelTargets();
    } catch (error) {
      toast.error("Erro ao guardar: " + getApiErrorMessage(error));
    }
  };

  const deleteRappelTarget = async (clientNumber) => {
    if (!window.confirm("Eliminar este distribuidor do Rappel?")) return;
    try {
      await axios.delete(`${API}/rappel/targets/${clientNumber}`);
      toast.success("Distribuidor eliminado");
      await fetchRappelTargets();
    } catch (error) {
      toast.error("Erro ao eliminar");
    }
  };

  const createRappelTarget = async () => {
    if (!rappelNewForm.client_number || !rappelNewForm.client_name) {
      toast.error("Número e nome são obrigatórios");
      return;
    }
    const tiers = rappelNewForm.tiers
      .filter(t => t.objetivo && t.percentual)
      .map(t => ({ objetivo: parseFloat(t.objetivo), percentual: parseFloat(t.percentual) / 100 }));
    if (tiers.length === 0) {
      toast.error("Adicione pelo menos um escalão (Objetivo + Percentual)");
      return;
    }
    try {
      await axios.post(`${API}/rappel/targets`, {
        client_number: rappelNewForm.client_number,
        client_name: rappelNewForm.client_name,
        tiers
      });
      toast.success("Distribuidor adicionado");
      setShowRappelAddForm(false);
      setRappelNewForm({ client_number: '', client_name: '', tiers: emptyRappelTiers() });
      await fetchRappelTargets();
    } catch (error) {
      toast.error("Erro ao adicionar: " + getApiErrorMessage(error));
    }
  };

  // Biometric handlers
  const handleEnableBiometric = async () => {
    setEnablingBiometric(true);
    try {
      const success = await enableBiometric();
      if (success) {
        toast.success('Face ID / Touch ID ativado');
      } else {
        toast.error('Não foi possível ativar');
      }
    } catch (error) {
      toast.error('Erro ao ativar biometria');
    } finally {
      setEnablingBiometric(false);
    }
  };

  const handleDisableBiometric = () => {
    disableBiometric();
    toast.success('Face ID / Touch ID desativado');
  };

  // PIN Dialog handlers
  const openPinDialog = (action) => {
    setPinAction(action);
    setPinStep(action === 'set' ? 1 : 1);
    setPinValue('');
    setPinConfirm('');
    setCurrentPin('');
    setPinError('');
    setShowPin(false);
    setShowPinDialog(true);
  };

  const handlePinInput = (digit) => {
    if (pinAction === 'change' || pinAction === 'remove') {
      if (pinStep === 1 && currentPin.length < 4) {
        setCurrentPin(prev => prev + digit);
        setPinError('');
      } else if (pinStep === 2 && pinValue.length < 4) {
        setPinValue(prev => prev + digit);
        setPinError('');
      } else if (pinStep === 3 && pinConfirm.length < 4) {
        setPinConfirm(prev => prev + digit);
        setPinError('');
      }
    } else {
      if (pinStep === 1 && pinValue.length < 4) {
        setPinValue(prev => prev + digit);
        setPinError('');
      } else if (pinStep === 2 && pinConfirm.length < 4) {
        setPinConfirm(prev => prev + digit);
        setPinError('');
      }
    }
  };

  const handlePinBackspace = () => {
    if (pinAction === 'change' || pinAction === 'remove') {
      if (pinStep === 1) setCurrentPin(prev => prev.slice(0, -1));
      else if (pinStep === 2) setPinValue(prev => prev.slice(0, -1));
      else if (pinStep === 3) setPinConfirm(prev => prev.slice(0, -1));
    } else {
      if (pinStep === 1) setPinValue(prev => prev.slice(0, -1));
      else if (pinStep === 2) setPinConfirm(prev => prev.slice(0, -1));
    }
    setPinError('');
  };

  const handlePinContinue = () => {
    if (pinAction === 'set') {
      if (pinStep === 1) {
        setPinStep(2);
      } else if (pinStep === 2) {
        if (pinValue !== pinConfirm) {
          setPinError(t('settings_pin_mismatch') || 'Os PINs não coincidem');
          setPinConfirm('');
          return;
        }
        setPin(pinValue);
        setShowPinDialog(false);
        toast.success(t('settings_pin_set') || 'PIN definido com sucesso');
      }
    } else if (pinAction === 'change') {
      if (pinStep === 1) {
        if (currentPin !== settings.pin) {
          setPinError(t('settings_pin_incorrect') || 'PIN atual incorreto');
          setCurrentPin('');
          return;
        }
        setPinStep(2);
      } else if (pinStep === 2) {
        setPinStep(3);
      } else if (pinStep === 3) {
        if (pinValue !== pinConfirm) {
          setPinError(t('settings_pin_mismatch') || 'Os PINs não coincidem');
          setPinConfirm('');
          return;
        }
        setPin(pinValue);
        setShowPinDialog(false);
        toast.success(t('settings_pin_changed') || 'PIN alterado com sucesso');
      }
    } else if (pinAction === 'remove') {
      if (currentPin !== settings.pin) {
        setPinError(t('settings_pin_incorrect') || 'PIN incorreto');
        setCurrentPin('');
        return;
      }
      removePin();
      setShowPinDialog(false);
      toast.success(t('settings_pin_removed') || 'PIN removido com sucesso');
    }
  };

  const getCurrentPinDisplay = () => {
    if (pinAction === 'change' || pinAction === 'remove') {
      if (pinStep === 1) return currentPin;
      if (pinStep === 2) return pinValue;
      if (pinStep === 3) return pinConfirm;
    }
    return pinStep === 1 ? pinValue : pinConfirm;
  };

  const getPinDialogTitle = () => {
    if (pinAction === 'remove') return t('settings_enter_pin') || 'Introduza o PIN atual';
    if (pinAction === 'change') {
      if (pinStep === 1) return t('settings_enter_current_pin') || 'Introduza o PIN atual';
      if (pinStep === 2) return t('settings_enter_new_pin') || 'Introduza o novo PIN';
      return t('settings_confirm_new_pin') || 'Confirme o novo PIN';
    }
    return pinStep === 1 
      ? (t('settings_enter_new_pin') || 'Introduza um PIN de 4 dígitos')
      : (t('settings_confirm_pin') || 'Confirme o PIN');
  };

  const handleExportData = async () => {
    try {
      const response = await axios.get(`${API}/export/all`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `gresart_export_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t('settings_export_success') || 'Dados exportados com sucesso');
    } catch (error) {
      toast.error(t('common_error') || 'Erro ao exportar dados');
    }
  };

  const handleClearCache = () => {
    clearCache();
    toast.success(t('settings_cache_cleared') || 'Cache limpo com sucesso');
  };

  // Section configuration
  const sections = [
    {
      id: 'security',
      title: t('settings_security') || 'Segurança',
      subtitle: settings.pinEnabled ? (t('settings_pin_enabled') || 'PIN ativo') : (t('settings_pin_disabled') || 'PIN inativo'),
      icon: Shield,
      iconBg: 'bg-amber-100 dark:bg-amber-900/30',
      iconColor: 'text-amber-600'
    },
    {
      id: 'biometric',
      title: 'Face ID / Touch ID',
      subtitle: settings.biometricEnabled ? 'Ativo' : 'Configurar',
      icon: BiometricIcon,
      iconBg: 'bg-purple-100 dark:bg-purple-900/30',
      iconColor: 'text-purple-600',
      hidden: !settings.pinEnabled || !biometricAvailable
    },
    {
      id: 'language',
      title: t('settings_language') || 'Idioma',
      subtitle: languages.find(l => l.code === settings.language)?.name || 'Português',
      icon: Translate,
      iconBg: 'bg-blue-100 dark:bg-blue-900/30',
      iconColor: 'text-blue-600'
    },
    {
      id: 'chatbot',
      title: 'Chatbot IA',
      subtitle: `${pdfTexts.length} catálogos indexados`,
      icon: Gear,
      iconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
      iconColor: 'text-emerald-600'
    },
    {
      id: 'data',
      title: t('settings_data') || 'Dados',
      subtitle: t('settings_data_desc') || 'Exportar e limpar dados',
      icon: CloudArrowDown,
      iconBg: 'bg-teal-100 dark:bg-teal-900/30',
      iconColor: 'text-teal-600'
    },
    {
      id: 'sales_upload',
      title: 'Dados de Vendas (GPP)',
      subtitle: vendasLastUpdated ? `Atualizado ${new Date(vendasLastUpdated).toLocaleDateString('pt-PT')}` : 'Nenhum dado carregado',
      icon: ChartLineUp,
      iconBg: 'bg-cyan-100 dark:bg-cyan-900/30',
      iconColor: 'text-cyan-600'
    },
    {
      id: 'price_list',
      title: 'Actualizar Lista de Preços',
      subtitle: priceListMeta?.last_updated ? `Atualizado ${new Date(priceListMeta.last_updated).toLocaleDateString('pt-PT')}` : 'Nenhuma atualização registada',
      icon: Tag,
      iconBg: 'bg-violet-100 dark:bg-violet-900/30',
      iconColor: 'text-violet-600',
      hidden: isReadOnly
    },
    {
      id: 'rappel',
      title: 'Objetivos de Rappel',
      subtitle: `${rappelTargets.length} distribuidor(es)`,
      icon: Percent,
      iconBg: 'bg-orange-100 dark:bg-orange-900/30',
      iconColor: 'text-orange-600',
      hidden: isReadOnly
    },
    {
      id: 'alerts',
      title: 'Gestão de Alertas',
      subtitle: `Visitas: ${alertSettings.visit_frequency_days}d`,
      icon: Bell,
      iconBg: 'bg-red-100 dark:bg-red-900/30',
      iconColor: 'text-red-600',
      hidden: isReadOnly
    }
  ];

  // Render section content
  const renderSectionContent = () => {
    switch (activeSection) {
      case 'security':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                settings.pinEnabled 
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                  : 'bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400'
              }`}>
                {settings.pinEnabled ? t('settings_pin_enabled') : t('settings_pin_disabled')}
              </span>
            </div>
            <p className="text-sm text-stone-500 dark:text-stone-400">
              {t('settings_security_desc') || 'Proteja a Secção Privada com um PIN de 4 dígitos'}
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              {settings.pinEnabled ? (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => openPinDialog('change')}
                    data-testid="btn-change-pin"
                  >
                    {t('settings_change_pin') || 'Alterar PIN'}
                  </Button>
                  <Button 
                    variant="outline" 
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    onClick={() => openPinDialog('remove')}
                    data-testid="btn-remove-pin"
                  >
                    {t('settings_remove_pin') || 'Remover PIN'}
                  </Button>
                </>
              ) : (
                <Button 
                  className="btn-primary"
                  onClick={() => openPinDialog('set')}
                  data-testid="btn-set-pin"
                >
                  {t('settings_set_pin') || 'Definir PIN'}
                </Button>
              )}
            </div>
          </div>
        );

      case 'biometric':
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-4">
              <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                settings.biometricEnabled 
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                  : 'bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400'
              }`}>
                {settings.biometricEnabled ? 'Ativo' : 'Inativo'}
              </span>
            </div>
            <div className="flex flex-wrap gap-2 pt-2">
              {settings.biometricEnabled ? (
                <Button 
                  variant="outline" 
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  onClick={handleDisableBiometric}
                  data-testid="btn-disable-biometric"
                >
                  Desativar
                </Button>
              ) : (
                <Button 
                  className="btn-primary"
                  onClick={handleEnableBiometric}
                  disabled={enablingBiometric}
                  data-testid="btn-enable-biometric"
                >
                  {enablingBiometric ? 'A ativar...' : 'Configurar'}
                </Button>
              )}
            </div>
          </div>
        );

      case 'language':
        return (
          <div className="space-y-4">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              {t('settings_language_desc') || 'Selecione o idioma da aplicação'}
            </p>
            <Select 
              value={settings.language} 
              onValueChange={(val) => updateSetting('language', val)}
            >
              <SelectTrigger className="w-full max-w-[250px]" data-testid="select-language">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code} data-testid={`lang-${lang.code}`}>
                    <span className="flex items-center gap-2">
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'chatbot':
        return (
          <div className="space-y-4">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              Carregar catálogos PDF para que o chatbot possa responder perguntas sobre produtos, preços e coleções.
            </p>
            
            {/* Current indexed PDFs */}
            <div className="mt-4">
              <h4 className="text-sm font-medium text-stone-700 dark:text-stone-300 mb-2">
                Catálogos Indexados ({pdfTexts.length})
              </h4>
              {loadingPdfs ? (
                <div className="text-sm text-stone-500">A carregar...</div>
              ) : pdfTexts.length === 0 ? (
                <div className="text-sm text-amber-600 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                  ⚠️ Nenhum catálogo indexado. O chatbot não conseguirá responder perguntas sobre os catálogos PDF.
                </div>
              ) : (
                <div className="space-y-2">
                  {pdfTexts.map((pdf, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-stone-50 dark:bg-stone-800 rounded-lg">
                      <div className="flex items-center gap-2 min-w-0">
                        <FilePdf size={18} className="text-red-500 flex-shrink-0" />
                        <span className="text-sm font-medium truncate">{pdf.pdf_name}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <span className="text-xs text-stone-400">
                          {(pdf.char_count / 1000).toFixed(1)}k chars
                        </span>
                        <button
                          onClick={() => deletePdfText(pdf.pdf_name)}
                          className="text-stone-500 hover:text-red-500 transition-colors p-1"
                          data-testid={`btn-delete-doc-${idx}`}
                          title="Eliminar este documento"
                          aria-label={`Eliminar documento ${pdf.pdf_name}`}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-4">
              <Button 
                className="btn-primary"
                onClick={loadAllDefaultPdfs}
                disabled={loadingPdfExtraction}
                data-testid="btn-load-pdfs"
              >
                {loadingPdfExtraction ? (
                  <>A carregar...</>
                ) : (
                  <>
                    <FilePdf size={16} className="mr-1" />
                    Carregar Todos os Catálogos
                  </>
                )}
              </Button>
              {pdfTexts.length > 0 && (
                <Button 
                  variant="outline" 
                  className="text-red-500 hover:text-red-600 hover:bg-red-50"
                  onClick={clearPdfTexts}
                  data-testid="btn-clear-pdfs"
                >
                  <Trash size={16} className="mr-1" />
                  Limpar Tudo
                </Button>
              )}
            </div>
            
            {/* Custom PDF Upload Section */}
            <div className="mt-6 pt-6 border-t border-stone-200 dark:border-stone-700">
              <h4 className="text-sm font-medium text-stone-700 dark:text-stone-300 mb-3">
                Adicionar PDF Personalizado (Lista de Preços, etc.)
              </h4>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-stone-500">Nome do documento</Label>
                  <Input
                    placeholder="Ex: Lista de Preços Cerâmica 2026"
                    value={customPdfName}
                    onChange={(e) => setCustomPdfName(e.target.value)}
                    className="mt-1"
                    data-testid="input-custom-pdf-name"
                  />
                </div>
                <div>
                  <Label className="text-xs text-stone-500">URL do PDF</Label>
                  <Input
                    placeholder="https://exemplo.com/lista-precos.pdf"
                    value={customPdfUrl}
                    onChange={(e) => setCustomPdfUrl(e.target.value)}
                    className="mt-1"
                    data-testid="input-custom-pdf-url"
                  />
                </div>
                <Button 
                  variant="outline"
                  onClick={loadCustomPdf}
                  disabled={loadingCustomPdf || !customPdfUrl || !customPdfName}
                  data-testid="btn-load-custom-pdf"
                >
                  {loadingCustomPdf ? 'A carregar...' : 'Adicionar por URL'}
                </Button>
              </div>
              
              {/* Divider */}
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-stone-200 dark:border-stone-700" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-white dark:bg-stone-900 px-2 text-stone-400">ou</span>
                </div>
              </div>
              
              {/* File Upload Option */}
              <div>
                <input
                  type="file"
                  ref={pdfFileInputRef}
                  onChange={handlePdfFileUpload}
                  accept=".pdf,.xlsx,.xls"
                  multiple
                  className="hidden"
                  data-testid="input-upload-file"
                />
                <Button 
                  variant="outline"
                  onClick={() => pdfFileInputRef.current?.click()}
                  disabled={uploadingPdf}
                  className="w-full"
                  data-testid="btn-upload-file"
                >
                  {uploadingPdf ? 'A processar...' : '📁 Carregar PDF ou Excel do dispositivo (pode selecionar vários)'}
                </Button>
                <p className="text-xs text-stone-400 mt-1">
                  Selecione um ou vários ficheiros PDF ou Excel (.xlsx) do seu computador ou telemóvel
                </p>
              </div>
              
              <p className="text-xs text-stone-400 mt-2">
                Pode adicionar listas de preços, tabelas de produtos, packing lists, ou qualquer documento com informação que queira consultar via chatbot.
              </p>
            </div>
            
            <p className="text-xs text-stone-400 mt-4">
              Após carregar, o chatbot poderá responder sobre: coleções, cores, formatos, preços, pesos, m²/palete, etc.
            </p>
          </div>
        );

      case 'data':
        return (
          <div className="space-y-4">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              {t('settings_data_desc') || 'Gerir dados da aplicação'}
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button 
                variant="outline" 
                onClick={handleExportData}
                data-testid="btn-export-data"
              >
                <CloudArrowDown size={16} className="mr-1" />
                {t('settings_export') || 'Exportar Dados'}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleClearCache}
                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                data-testid="btn-clear-cache"
              >
                <Trash size={16} className="mr-1" />
                {t('settings_clear_cache') || 'Limpar Cache'}
              </Button>
            </div>
          </div>
        );

      case 'sales_upload':
        return (
          <div className="space-y-4">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              Carregue os ficheiros GPP_Monthly_Sales (PDF) e GPP_Hit_Parade (Excel/PDF) para atualizar a Análise de Vendas.
            </p>
            {vendasLastUpdated && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-4 py-2 text-sm text-blue-700 dark:text-blue-300">
                Última atualização: <strong>{new Date(vendasLastUpdated).toLocaleString('pt-PT')}</strong>
              </div>
            )}
            <div
              className="border-2 border-dashed border-stone-300 dark:border-stone-700 rounded-lg p-6 text-center hover:border-[#009392] transition-colors cursor-pointer"
              onClick={() => vendasFileInputRef.current?.click()}
              data-testid="settings-vendas-dropzone"
            >
              <input
                ref={vendasFileInputRef}
                type="file"
                multiple
                accept=".pdf,.xlsx,.xls"
                onChange={handleVendasFileUpload}
                className="hidden"
                data-testid="settings-vendas-file-input"
              />
              <Upload size={40} className="mx-auto text-stone-400 mb-2" />
              <p className="text-stone-600 dark:text-stone-300 font-medium">Clique para carregar ficheiros GPP</p>
              <p className="text-stone-400 text-sm mt-1">GPP_Monthly_Sales.pdf, GPP_Hit_Parade_*.xlsx/pdf</p>
            </div>

            {vendasFiles.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-stone-600 dark:text-stone-300">Ficheiros carregados:</h3>
                {vendasFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-stone-50 dark:bg-stone-800 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      {getVendasFileIcon(file.name)}
                      <div>
                        <p className="text-sm font-medium text-stone-700 dark:text-stone-200">{file.name}</p>
                        <p className="text-xs text-stone-400">{(file.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeVendasFile(index)}
                      className="p-1 hover:bg-red-100 rounded-full text-stone-400 hover:text-red-500 transition-colors"
                      data-testid={`settings-vendas-remove-file-${index}`}
                    >
                      <X size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {vendasFiles.length > 0 && (
              <Button
                onClick={parseVendasData}
                disabled={parsingVendas}
                className="w-full bg-gradient-to-r from-[#009392] to-[#00b3b0] hover:from-[#007a7a] hover:to-[#009392]"
                data-testid="settings-parse-vendas-btn"
              >
                {parsingVendas ? (
                  <>
                    <LoadingSpinner size={20} className="mr-2" />
                    Processando...
                  </>
                ) : (
                  <>
                    <ChartLineUp size={20} className="mr-2" />
                    Processar Dados de Vendas
                  </>
                )}
              </Button>
            )}
          </div>
        );


      case 'price_list':
        return (
          <div className="space-y-4">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              Carregue um novo ficheiro Excel com a lista de preços atualizada. Os preços e restantes dados das referências existentes são substituídos, novas referências são adicionadas automaticamente, e referências que deixaram de existir pedem confirmação antes de serem removidas.
            </p>
            {priceListMeta?.last_updated && (
              <div className="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg px-4 py-2 text-sm text-violet-700 dark:text-violet-300" data-testid="price-list-last-update">
                Última atualização: <strong>{new Date(priceListMeta.last_updated).toLocaleString('pt-PT')}</strong>
                {priceListMeta.updated_by && <> por <strong>{priceListMeta.updated_by}</strong></>}
                <div className="text-xs mt-1 text-violet-600 dark:text-violet-400">
                  {priceListMeta.added_count || 0} adicionado(s) · {priceListMeta.updated_count || 0} atualizado(s) · {priceListMeta.removed_count || 0} removido(s)
                </div>
              </div>
            )}
            <div>
              <input
                ref={priceListFileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handlePriceListFileSelect}
                className="hidden"
                data-testid="input-price-list-upload"
              />
              <Button
                variant="outline"
                onClick={() => priceListFileInputRef.current?.click()}
                disabled={uploadingPriceList}
                className="w-full"
                data-testid="btn-upload-price-list"
              >
                {uploadingPriceList ? 'A processar...' : 'Carregar Novo Ficheiro Excel'}
              </Button>
              <p className="text-xs text-stone-400 mt-1">
                Colunas esperadas: Descrição Produto, Cor, Formato, Acabamento/Superfície, Espessura, Série, Marca, Tipo de Utilização, Preço, Moeda, Grupo Preço, Unidade de Medida, Peças por Caixa, m2 por Caixa, kg por Caixa, Caixas por palete, M2 por palete, Kg por palete, Qualidade, EAN, País de Origem.
              </p>
            </div>
          </div>
        );

      case 'rappel':
        return (
          <div className="space-y-4">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              Carregue um ficheiro anual (Excel recomendado, PDF em modo experimental) com os objetivos de Rappel por distribuidor, ou edite manualmente os escalões existentes.
            </p>

            <div>
              <input
                ref={rappelFileInputRef}
                type="file"
                accept=".xlsx,.xls,.pdf"
                onChange={handleRappelFileUpload}
                className="hidden"
                data-testid="input-rappel-upload"
              />
              <Button
                variant="outline"
                onClick={() => rappelFileInputRef.current?.click()}
                disabled={uploadingRappelFile}
                className="w-full"
                data-testid="btn-upload-rappel-excel"
              >
                {uploadingRappelFile ? 'A importar...' : 'Carregar ficheiro anual de Rappel (Excel ou PDF)'}
              </Button>
              <p className="text-xs text-stone-400 mt-1">
                Excel - colunas esperadas: Numero, Nome, Objetivo1, Percentual1, Objetivo2, Percentual2, Objetivo3, Percentual3
              </p>
            </div>

            <div className="pt-4 border-t border-stone-200 dark:border-stone-700">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-stone-700 dark:text-stone-300">
                  Distribuidores ({rappelTargets.length})
                </h4>
                <Button size="sm" variant="outline" onClick={() => setShowRappelAddForm(!showRappelAddForm)} data-testid="btn-toggle-add-rappel">
                  <Plus size={14} className="mr-1" /> Novo
                </Button>
              </div>

              {showRappelAddForm && (
                <div className="p-3 mb-3 rounded-lg bg-stone-50 dark:bg-stone-800 space-y-2" data-testid="rappel-add-form">
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Número" value={rappelNewForm.client_number}
                      onChange={(e) => setRappelNewForm(prev => ({ ...prev, client_number: e.target.value }))}
                      data-testid="input-new-rappel-number" />
                    <Input placeholder="Nome" value={rappelNewForm.client_name}
                      onChange={(e) => setRappelNewForm(prev => ({ ...prev, client_name: e.target.value }))}
                      data-testid="input-new-rappel-name" />
                  </div>
                  {rappelNewForm.tiers.map((tier, idx) => (
                    <div key={idx} className="grid grid-cols-2 gap-2">
                      <Input placeholder={`Objetivo Escalão ${idx + 1} (€)`} type="number" value={tier.objetivo}
                        onChange={(e) => setRappelNewForm(prev => ({ ...prev, tiers: prev.tiers.map((t, i) => i === idx ? { ...t, objetivo: e.target.value } : t) }))}
                        data-testid={`input-new-rappel-objetivo-${idx}`} />
                      <Input placeholder={`% Escalão ${idx + 1}`} type="number" value={tier.percentual}
                        onChange={(e) => setRappelNewForm(prev => ({ ...prev, tiers: prev.tiers.map((t, i) => i === idx ? { ...t, percentual: e.target.value } : t) }))}
                        data-testid={`input-new-rappel-percentual-${idx}`} />
                    </div>
                  ))}
                  <Button size="sm" className="btn-primary w-full" onClick={createRappelTarget} data-testid="btn-save-new-rappel">
                    Guardar Distribuidor
                  </Button>
                </div>
              )}

              {loadingRappelTargets ? (
                <p className="text-sm text-stone-400">A carregar...</p>
              ) : rappelTargets.length === 0 ? (
                <p className="text-sm text-stone-400">Nenhum distribuidor configurado.</p>
              ) : (
                <div className="space-y-2">
                  {rappelTargets.map((target) => (
                    <div key={target.client_number} className="p-3 rounded-lg bg-stone-50 dark:bg-stone-800" data-testid={`rappel-target-${target.client_number}`}>
                      {editingRappelNumber === target.client_number ? (
                        <div className="space-y-2">
                          <Input value={rappelEditForm.client_name}
                            onChange={(e) => setRappelEditForm(prev => ({ ...prev, client_name: e.target.value }))}
                            data-testid={`input-edit-rappel-name-${target.client_number}`} />
                          {rappelEditForm.tiers.map((tier, idx) => (
                            <div key={idx} className="grid grid-cols-2 gap-2">
                              <Input placeholder={`Objetivo ${idx + 1}`} type="number" value={tier.objetivo}
                                onChange={(e) => setRappelEditForm(prev => ({ ...prev, tiers: prev.tiers.map((t, i) => i === idx ? { ...t, objetivo: e.target.value } : t) }))} />
                              <Input placeholder={`% ${idx + 1}`} type="number" value={tier.percentual}
                                onChange={(e) => setRappelEditForm(prev => ({ ...prev, tiers: prev.tiers.map((t, i) => i === idx ? { ...t, percentual: e.target.value } : t) }))} />
                            </div>
                          ))}
                          <div className="flex gap-2">
                            <Button size="sm" className="btn-primary" onClick={saveEditRappel} data-testid={`btn-save-rappel-${target.client_number}`}>
                              <Check size={14} className="mr-1" /> Guardar
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingRappelNumber(null)}>
                              <X size={14} className="mr-1" /> Cancelar
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-stone-800 dark:text-stone-100 truncate">{target.client_name}</p>
                            <p className="text-xs text-stone-400">Nº {target.client_number}</p>
                            <p className="text-xs text-stone-500 mt-1">
                              {target.tiers.map((t, i) => `E${i + 1}: €${t.objetivo.toLocaleString('pt-PT')} (${(t.percentual * 100).toFixed(1)}%)`).join(' · ')}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button onClick={() => startEditRappel(target)} className="p-1.5 text-stone-400 hover:text-[#009392] rounded" data-testid={`btn-edit-rappel-${target.client_number}`}>
                              <PencilSimple size={16} />
                            </button>
                            <button onClick={() => deleteRappelTarget(target.client_number)} className="p-1.5 text-stone-400 hover:text-red-500 rounded" data-testid={`btn-delete-rappel-${target.client_number}`}>
                              <Trash size={16} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'alerts':
        return (
          <div className="space-y-6">
            <p className="text-sm text-stone-500 dark:text-stone-400">
              Configure a sensibilidade das alertas automáticas mostradas no ícone de Campana.
            </p>

            <div className="space-y-2">
              <Label>Frequência de visita (alerta se não houver visita neste período)</Label>
              <Select
                value={String(alertSettings.visit_frequency_days)}
                onValueChange={(val) => saveAlertSettings({ ...alertSettings, visit_frequency_days: parseInt(val) })}
                disabled={savingAlertSettings}
              >
                <SelectTrigger data-testid="select-visit-frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="30">30 dias</SelectItem>
                  <SelectItem value="45">45 dias</SelectItem>
                  <SelectItem value="60">60 dias</SelectItem>
                  <SelectItem value="90">90 dias</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 pb-24 md:pb-6 max-w-2xl mx-auto" data-testid="settings-page">
      {/* Header */}
      <PageHeader
        icon={Gear}
        title={activeSection ? sections.find(s => s.id === activeSection)?.title : (t('settings_title') || 'Configurações')}
        onBack={activeSection ? () => setActiveSection(null) : undefined}
      />

      {/* Content */}
      {activeSection ? (
        <div className="gresart-card dark:bg-stone-900 dark:border-stone-700">
          {renderSectionContent()}
        </div>
      ) : (
        <div className="space-y-3">
          {sections.filter(s => !s.hidden).map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className="w-full gresart-card dark:bg-stone-900 dark:border-stone-700 hover:shadow-md transition-all flex items-center gap-4 text-left"
              data-testid={`settings-btn-${section.id}`}
              data-section={section.id}
            >
              <div className={`p-3 rounded-xl ${section.iconBg} ${section.iconColor}`}>
                <section.icon size={24} weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-stone-900 dark:text-white">{section.title}</h3>
                <p className="text-sm text-stone-500 dark:text-stone-400 truncate">{section.subtitle}</p>
              </div>
              <CaretRight size={20} className="text-stone-400 flex-shrink-0" />
            </button>
          ))}
        </div>
      )}

      {/* PIN Dialog */}
      <Dialog open={showPinDialog} onOpenChange={setShowPinDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-[Manrope] text-center">
              {getPinDialogTitle()}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-6 space-y-6">
            {/* PIN Display */}
            <div className="flex justify-center gap-3">
              {[0, 1, 2, 3].map((i) => {
                const display = getCurrentPinDisplay();
                const filled = display.length > i;
                return (
                  <div
                    key={i}
                    className={`w-14 h-14 rounded-xl border-2 flex items-center justify-center text-2xl font-bold transition-all ${
                      filled 
                        ? 'border-[#00A19A] bg-[#00A19A]/10 text-[#00A19A]' 
                        : 'border-stone-300 dark:border-stone-600'
                    }`}
                  >
                    {filled ? (showPin ? display[i] : '•') : ''}
                  </div>
                );
              })}
            </div>

            {/* Show/Hide PIN toggle */}
            <button
              onClick={() => setShowPin(!showPin)}
              className="mx-auto flex items-center gap-2 text-sm text-stone-500 hover:text-stone-700"
            >
              {showPin ? <EyeSlash size={18} /> : <Eye size={18} />}
              {showPin ? 'Ocultar' : 'Mostrar'}
            </button>

            {/* Error message */}
            {pinError && (
              <p className="text-center text-red-500 text-sm font-medium">{pinError}</p>
            )}

            {/* Simple text input alternative */}
            <div className="text-center">
              <Label className="text-sm text-stone-500 dark:text-stone-400">
                {t('settings_pin_or_type') || 'Ou introduza diretamente:'}
              </Label>
              <Input
                type={showPin ? 'text' : 'password'}
                maxLength={4}
                value={getCurrentPinDisplay()}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                  if (pinAction === 'change' || pinAction === 'remove') {
                    if (pinStep === 1) setCurrentPin(val);
                    else if (pinStep === 2) setPinValue(val);
                    else if (pinStep === 3) setPinConfirm(val);
                  } else {
                    if (pinStep === 1) setPinValue(val);
                    else if (pinStep === 2) setPinConfirm(val);
                  }
                  setPinError('');
                }}
                className="mt-2 text-center text-2xl tracking-[0.5em] max-w-[160px] mx-auto"
                placeholder="••••"
                data-testid="pin-input-text"
              />
            </div>

            {/* Numeric Keypad */}
            <NumericKeypad onInput={handlePinInput} onBackspace={handlePinBackspace} />

            {/* Continue Button */}
            <Button
              className="w-full btn-primary"
              disabled={
                (pinAction === 'remove' && currentPin.length !== 4) ||
                (pinAction === 'set' && ((pinStep === 1 && pinValue.length !== 4) || (pinStep === 2 && pinConfirm.length !== 4))) ||
                (pinAction === 'change' && (
                  (pinStep === 1 && currentPin.length !== 4) ||
                  (pinStep === 2 && pinValue.length !== 4) ||
                  (pinStep === 3 && pinConfirm.length !== 4)
                ))
              }
              onClick={handlePinContinue}
              data-testid="btn-pin-continue"
            >
              {pinAction === 'remove' 
                ? (t('settings_remove_pin') || 'Remover PIN')
                : (pinStep === (pinAction === 'change' ? 3 : 2) ? (t('common_save') || 'Guardar') : (t('settings_pin_next') || 'Continuar'))
              }
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Price List Removal Confirmation Dialog */}
      <Dialog open={showPriceListRemovalDialog} onOpenChange={(open) => { if (!open) cancelPriceListRemoval(); }}>
        <DialogContent className="sm:max-w-lg" data-testid="price-list-removal-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Manrope] flex items-center gap-2">
              <Warning size={22} className="text-amber-500" />
              Confirmar Remoção de Referências
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-stone-600 dark:text-stone-300">
              O novo ficheiro não inclui <strong>{priceListRemovalPreview?.to_remove?.length || 0}</strong> referência(s) que existem atualmente na Lista de Preços. Confirma a remoção destas referências?
            </p>
            <div className="max-h-56 overflow-y-auto space-y-1 bg-stone-50 dark:bg-stone-800 rounded-lg p-3" data-testid="price-list-removal-list">
              {(priceListRemovalPreview?.to_remove || []).map((p) => (
                <div key={p.product_code} className="text-sm flex justify-between gap-2">
                  <span className="text-stone-700 dark:text-stone-200 truncate">{p.product_name || "Sem nome"}</span>
                  <span className="text-stone-400 flex-shrink-0">{p.product_code}</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-stone-400">
              {priceListRemovalPreview?.to_add_count || 0} referência(s) nova(s) serão adicionadas e {priceListRemovalPreview?.to_update_count || 0} serão atualizadas, independentemente desta escolha.
            </p>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={cancelPriceListRemoval} data-testid="btn-cancel-price-list-removal">
                Cancelar
              </Button>
              <Button
                className="bg-red-500 hover:bg-red-600 text-white"
                onClick={confirmPriceListRemoval}
                disabled={uploadingPriceList}
                data-testid="btn-confirm-price-list-removal"
              >
                {uploadingPriceList ? 'A processar...' : 'Confirmar e Remover'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
